import React, { useState, useEffect, useContext } from 'react';
import {
  params, string, arrayOf,
  shape, useDataModel, extend
} from '@thd-nucleus/data-sources';
import PropTypes from 'prop-types';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductImage,
  PodFooter,
  PodSection,
  PodSpacer
} from '@thd-olt-component-react/product-pod';
import { Button } from '@one-thd/sui-atomic-components';
import { ProductQuantity } from '@thd-olt-component-react/quantity';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { AddToContext } from '../../AddToContext';
import { SCREENS } from '../../AddToConstants';

export const AddToProductPod = ({
  itemId,
  index,
  parent,
  storeId,
  onClick,
  isMobile,
  updateList,
  quantity: quantityProp,
  basePrice,
  skuNumber,
  isIntent = false,
  handleResolveIntent
}) => {
  const [productSelection, setProductSelection] = useState({ itemId: null, quantity: null });
  const [qty, setQty] = useState(quantityProp ?? 1);
  const [nextScreenName, setNextScreenName] = useState(null);
  const {
    setScreen, setSelectedProductDetails, products, addToType, screen, setShowSuccessMessage, setShowErrorMessage
  } = useContext(AddToContext);
  const analyticsData = {
    position: index,
    parent
  };
  const {
    PROJECT_SELECT_GROUP,
    MINI_PIP_BASE
  } = SCREENS;

  const { data: productData, error, loading } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId
  });

  const onQtyUpdate = ({ quantity }) => {
    setQty(quantity);
  };

  const handleSelectedProductClick = async (item, itemQuantity, screenName) => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setProductSelection({ itemId: item, quantity: itemQuantity });
    if (addToType === 'Project' && screenName !== MINI_PIP_BASE) {
      if (isIntent) {
        await handleResolveIntent({
          omsId: productData?.product?.identifiers?.itemId,
          sku: productData?.product?.identifiers?.storeSkuNumber
        });
        return;
      }
      setNextScreenName(PROJECT_SELECT_GROUP);
    } else if (addToType === 'List' && screenName !== MINI_PIP_BASE) {
      updateList({
        variables: {
          products: [{ itemId: item, quantity: itemQuantity }]
        }
      });
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.addToAction', {
        name: 'list add',
        location: 'lists: mini plp',
        items: [{
          quantity: itemQuantity,
          // Does this need to be basePrice * quantity?
          price: basePrice || 'N/A',
          sku: item || 'N/A'
        }],
        isBulkeAction: false
      });
    } else {
      setNextScreenName(screenName);
    }
  };

  useEffect(() => {
    if (productData?.product?.itemId && nextScreenName && productSelection) {
      setSelectedProductDetails({ itemId: productSelection.itemId, quantity: productSelection.quantity, productData });
      setScreen(nextScreenName);
    }
  }, [productData, productSelection]);

  return (
    <ProductPod
      itemId={itemId}
      analyticsData={analyticsData}
      render={(pod) => {
        return (
          <>
            <PodSection columnAlign alignTop>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                className="sui-cursor-pointer"
                onClick={() => handleSelectedProductClick(itemId, qty, MINI_PIP_BASE)}
                data-testid="mini-pip-nav-container"
              >
                <div className="sui-pointer-events-none">
                  <ProductImage itemId={pod.itemId} target="_blank" />
                  <ProductHeader
                    brand="above"
                    itemId={pod.itemId}
                    brandTitleMaxLine={4}
                    disableShopThisCollection
                    onClick={onClick}
                    target="_blank"
                  />
                  <PodSpacer disable={isMobile}>
                    <ProductRating itemId={pod.itemId} onClick={onClick} target="_blank" />
                  </PodSpacer>
                  <PodSpacer minHeight="3.75rem" padding={['TOP']} disable={isMobile}>
                    <Price
                      itemId={pod.itemId}
                      large={false}
                      storeId={storeId}
                      displayEachUom={false}
                      showPreferredPricingBadge
                      hideForProductTypes={['HDQC']}
                    />
                  </PodSpacer>
                </div>
              </div>
            </PodSection>
            <PodFooter>
              <PodSpacer minHeight="11rem">
                <div className="sui-pointer-events-none">
                  <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} hideForChooseYourOptions />
                  <FulfillmentPodShipping itemId={pod.itemId} storeId={storeId} />
                </div>
              </PodSpacer>
              <PodSpacer minHeight="5rem" padding={['TOP', 'BOTTOM']}>
                <ProductQuantity
                  itemId={pod.itemId}
                  onQuantityChange={onQtyUpdate}
                  quantity={qty}
                />
              </PodSpacer>
              <Button
                fullWidth
                onClick={() => handleSelectedProductClick(itemId, qty)}
              >
                Add to {addToType}
              </Button>
            </PodFooter>
          </>
        );
      }}
    />
  );
};

AddToProductPod.propTypes = {
  itemId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  parent: PropTypes.string.isRequired,
  storeId: PropTypes.string,
  onClick: PropTypes.func,
  isMobile: PropTypes.bool,
  updateList: PropTypes.func,
  quantity: PropTypes.number,
  isIntent: PropTypes.bool,
  handleResolveIntent: PropTypes.func
};

AddToProductPod.defaultProps = {
  storeId: '',
  onClick: () => { },
  isMobile: false,
  updateList: () => { },
  quantity: null,
  isIntent: false,
  handleResolveIntent: () => { }
};

AddToProductPod.dataModel = extend(
  {
    product: params({
      itemId: string()
        .isRequired(),
      dataSource: string()
    }).shape({
      itemId: string(),
      dataSources: string(),
      identifiers: shape({
        brandName: string(),
        productLabel: string(),
        storeSkuNumber: string(),
        omsThdSku: string(),
        specialOrderSku: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      })
    })
  },
  ProductPod,
  ProductImage,
  ProductHeader,
  ProductRating,
  Price,
  ProductQuantity,
  ProductATC,
  FulfillmentPodStore,
  FulfillmentPodShipping
);
